import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  Chip,
  CircularProgress,  
  IconButton,
  Menu,
  MenuItem,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Tooltip,
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../components/hooks/useTableSortInit';
import TableTimeStamps from '../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../components/utils/Copyable';
import TopBar from '../../../../components/utils/TopBar';
import NA from '../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/Delete';

import { 
  GetEmailTemplatesBIZnotifications,
  DeleteEmailTemplateBIZnotifications
} from '../../../../redux/actions/business/notifications/EmailTemplatesActions';

import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import EmailTemplatesModal from './EmailTemplatesModal';

import global from "../../../../global.scss";

const EmailTemplatesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.notifications"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("ALL");
  const [emailTemplatesModalOpen, setEmailTemplatesModalOpen] = useState(false);
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [emailTemplatesList, setEmailTemplatesList] = useState([]);
  const [emailTemplatesTotal, setEmailTemplatesTotal] = useState(0);  

  const [emailTemplatesMenuAnchor, setEmailTemplatesMenuAnchor] = useState(null);
  const [emailTemplatesMenuOpen, setEmailTemplatesMenuOpen] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Code",
      sort: "code",
    },
    {
      label: "Name",
      sort: "name",
    },
    {
      label: "Subject",
      sort: "subject",
    },
    {
      label: "Tag",
      sort: "tag",
    },
    {
      label: "Important",
      sort: "important",
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    },
    {
      label: "",
      sort: null,
    }
  ];

  useEffect(() => {
    handleGetEmailTemplates(setLoading);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection, tagFilter]);

  useEffect(() => {
    try {
      console.log(state);
      formatEmailTemplatesList(state.emailTemplatesList.records || []);
      setEmailTemplatesTotal(state.emailTemplatesList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatEmailTemplatesList = (list) => {
    setEmailTemplatesList(list)
  };

  async function handleGetEmailTemplates(customLoading) {
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    if(tagFilter && tagFilter !== "ALL") query += `&tag=${tagFilter}`;
    await dispatch(GetEmailTemplatesBIZnotifications(page+1, rowsPerPage, query, customLoading || setLoading));
  };

  async function handleGetEmailTemplate(template, customLoading) {
    setModalTitle("Edit " + template.name);
    setUpdate({
      id: template.id,
      defaultMode: 2
    });
    setEmailTemplatesModalOpen(!emailTemplatesModalOpen);
  }

  async function handleDelete(){
    setDeleteConfirmationModalOpen(false);
    dispatch(DeleteEmailTemplateBIZnotifications(deleteId, setDeleting)).then(() => {
      handleGetEmailTemplates(setLoading).then(() => {
        setDeleteId(null);
      })
    });
  };

  const getTagColor = (tag) => {
    switch (tag) {
      case "GENERAL": return "#9c27b0";
      case "BROADCAST": return "#ff9800";
      case "CC": return "#009688";
      case "WYZIA": return "#e91e63"; 
      case "INVOICE_PAYMENT_REMINDERS": return "#4caf50";
      case "TRADE_ALLOCATIONS": return "#2196f3";
      case "CHEQUE_PRINTING": return "#673ab7";

      default:
        return global['secondary_light'];
    }
  };

  return (
    <Box>

      <EmailTemplatesModal
        open={emailTemplatesModalOpen}
        setOpen={setEmailTemplatesModalOpen}
        title={modalTitle}
        update={update}
        setUpdate={setUpdate}
        handleGet={handleGetEmailTemplates}
        />

      <TopBar
        title={"Email Templates"}
        search={search}
        searchPlaceholder={"Search id, name, subject, tag or code"}
        onSearchChange={handleSearchChange}
        button1Text={"Add Email Template"}
        button1OnClick={() => {
          setModalTitle("Add Email Template");
          setUpdate({ defaultMode: 1 });
          setEmailTemplatesModalOpen(true);
        }}
        />

      <Box 
        className="flexCenter_Row" 
        mb={"15px"}
        gap={"15px"}>
        <Chip
          variant='filled'
          color={tagFilter === "ALL" ? 'success' : 'default'}
          label="ALL"
          onClick={() => {
            setTagFilter("ALL");
          }}
          />
        <Chip
          variant='filled'
          color={tagFilter === "GENERAL" ? 'success' : 'default'}
          label="GENERAL"
          onClick={() => {
            setTagFilter("GENERAL");
          }}
          />
        <Chip
          variant='filled'
          color={tagFilter === "BROADCAST" ? 'success' : 'default'}
          label="BROADCAST"
          onClick={() => {
            setTagFilter("BROADCAST");
          }}
          />
        <Chip
          variant='filled'
          color={tagFilter === "CC" ? 'success' : 'default'}
          label="CC"
          onClick={() => {
            setTagFilter("CC");
          }}
          />
        <Chip
          variant='filled'
          color={tagFilter === "WYZIA" ? 'success' : 'default'}
          label="WYZIA"
          onClick={() => {
            setTagFilter("WYZIA");
          }}
          />
        <Chip
          variant='filled'
          color={tagFilter === "INVOICE_PAYMENT_REMINDERS" ? 'success' : 'default'}
          label="INVOICE PAYMENT REMINDERS"
          onClick={() => {
            setTagFilter("INVOICE_PAYMENT_REMINDERS");
          }}
          />
        <Chip
          variant='filled'
          color={tagFilter === "TRADE_ALLOCATIONS" ? 'success' : 'default'}
          label="TRADE ALLOCATIONS"
          onClick={() => {
            setTagFilter("TRADE_ALLOCATIONS");
          }}
          />
        <Chip
          variant='filled'
          color={tagFilter === "CHEQUE_PRINTING" ? 'success' : 'default'}
          label="CHEQUE PRINTING"
          onClick={() => {
            setTagFilter("CHEQUE_PRINTING");
          }}
          />
      </Box>

      <ConfirmationModal
        open={deleteConfirmationModalOpen}
        setOpen={setDeleteConfirmationModalOpen}
        title={"Delete " + modalTitle}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
        />
      
      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          emailTemplatesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No email template found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                emailTemplatesList.map((row, index) => (
                  <TableRow 
                    key={index}
                    className="table__clickable-row"
                    onClick={() => {handleGetEmailTemplate(row, setLoading)}}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>   
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {
                        row.code
                        ? <Chip
                            size="small"
                            label={row.code}
                            sx={{ fontFamily: "monospace", fontWeight: 600 }}
                          />
                        // The code is what ties this template to its counterpart
                        // in another environment — without one it cannot be synced.
                        : <Tooltip title="No code — this template cannot be synced between environments">
                            <Chip size="small" label="no code" color="warning" variant="outlined" />
                          </Tooltip>
                      }
                    </TableCell>
                    <TableCell>{row.name || <NA />}</TableCell>
                    <TableCell>{row.subject || <NA />}</TableCell>  
                    <TableCell>
                      <Box
                        sx={{
                          padding: "2px 8px",
                          backgroundColor: getTagColor(row.tag),
                          color: "white",
                          borderRadius: "5px",
                          display: "inline-block"
                        }}>
                        {row.tag || <NA />}
                      </Box>
                    </TableCell>  
                    <TableCell>{row.important ? "YES" : "NO"}</TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                    <TableCell>
                      {
                        row.id === deleteId && deleting
                        ?
                        <CircularProgress size={26} sx={{ margin: "7px" }}/>
                        :
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setEmailTemplatesMenuAnchor(e.currentTarget);
                            setEmailTemplatesMenuOpen(true);
                            setUpdate(row);
                            setModalTitle(row.name || "");
                            setDeleteId(row.id);
                          }}>
                          <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                        </IconButton>
                      }
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[2, 5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={emailTemplatesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }

      <Menu
        open={emailTemplatesMenuOpen}
        anchorEl={emailTemplatesMenuAnchor}
        onClose={() => setEmailTemplatesMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setEmailTemplatesMenuOpen(false);
            handleGetEmailTemplate(update, setLoading);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setEmailTemplatesMenuOpen(false);
            setDeleteConfirmationModalOpen(true);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <DeleteIcon />
            Delete
          </Box>
        </MenuItem>
      </Menu>

    </Box>
  )
}

export default EmailTemplatesPage;